/**
 * useApiQuery — React Query wrapper with Api injection
 *
 * Wraps @tanstack/react-query's useQuery so that the queryFn receives
 * the Api instance from context automatically. Individual hooks never
 * need to call useApi() themselves.
 *
 * Pattern adapted from influenster-v4-mobile/src/api/hooks/useQuery.ts
 *
 * Usage:
 *   import { useApiQuery } from '@/api/hooks/useQuery';
 *   import { campaignKeys } from '@/api/queryKeys';
 *
 *   export function useCampaigns() {
 *     return useApiQuery({
 *       queryKey: campaignKeys.lists(),
 *       queryFn: (api) => () => api.campaigns.campaignsControllerGetUsersCampaignsList(),
 *     });
 *   }
 */

import {
  type DefaultError,
  type QueryFunction,
  type QueryKey,
  useQuery as useQueryBase,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApi } from '@/contexts/ApiContext';
import type { Api } from '@/api/Api';

export type ApiQueryFnResolver<TQueryFnData, TQueryKey extends QueryKey> = (
  api: Api,
) => QueryFunction<TQueryFnData, TQueryKey, never>;

interface UseApiQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> extends Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn'> {
  queryFn?: ApiQueryFnResolver<TQueryFnData, TQueryKey>;
}

type UseApiQuery = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseApiQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) => UseQueryResult<TData, TError>;

const useApiQuery: UseApiQuery = (apiQueryOptions) => {
  const api = useApi();
  const { queryFn: queryFnResolver, ...options } = apiQueryOptions;

  const queryFn = useMemo(
    () => (queryFnResolver ? queryFnResolver(api) : undefined),
    [api, queryFnResolver],
  );

  return useQueryBase({ ...options, queryFn });
};

export default useApiQuery;
export { useApiQuery };
